import { deleteAPI, getAPI, postAPI, putAPI } from "./Networking";
import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import {
  Category,
  CategoryUpsertPayload,
  GetCategoriesParams,
  normalizeCategoryListSortBy,
} from "../models/Categories/types";

/**
 * Category admin routes on this backend live under `/v1/categories`.
 * Override with `REACT_APP_CATEGORIES_API_BASE` if your deployment uses another path.
 */
const BASE_RAW =
  process.env.REACT_APP_CATEGORIES_API_BASE?.trim() || "/v1/categories";
const BASE = BASE_RAW.startsWith("/") ? BASE_RAW : `/${BASE_RAW}`;

/** Resolved categories path (after env); safe to show in UI copy. */
export const CATEGORY_API_BASE = BASE;

/** Coalesce API variants into `Category` fields used by the admin UI. */
function normalizeCategoryRecord(raw: unknown): Category {
  const r = raw as Record<string, unknown>;
  const pickStr = (v: unknown): string | null => {
    if (v == null) return null;
    const s = typeof v === "string" ? v.trim() : String(v).trim();
    return s.length ? s : null;
  };
  const firstStr = (...keys: string[]): string | null => {
    for (const k of keys) {
      const s = pickStr(r[k]);
      if (s) return s;
    }
    return null;
  };
  const strFromNested = (
    objKey: string,
    ...childKeys: string[]
  ): string | null => {
    const v = r[objKey];
    if (!v || typeof v !== "object" || Array.isArray(v)) return null;
    const o = v as Record<string, unknown>;
    for (const k of childKeys) {
      const s = pickStr(o[k]);
      if (s) return s;
    }
    return null;
  };
  const asBool = (v: unknown): boolean => {
    if (typeof v === "boolean") return v;
    if (v === 1 || v === "1" || v === "true") return true;
    if (v === 0 || v === "0" || v === "false") return false;
    return Boolean(v);
  };

  const id = Number(r.id);
  const desc = firstStr("desc", "description");

  const seo_title =
    firstStr(
      "seo_title",
      "seoTitle",
      "meta_title",
      "metaTitle",
    ) ??
    strFromNested("seo", "title", "seo_title", "seoTitle") ??
    strFromNested("meta", "title", "seo_title") ??
    strFromNested("seo_meta", "title");

  const seo_description =
    firstStr(
      "seo_description",
      "seoDescription",
      "meta_description",
      "metaDescription",
    ) ??
    strFromNested("seo", "description", "seo_description", "seoDescription") ??
    strFromNested("meta", "description", "seo_description") ??
    strFromNested("seo_meta", "description");

  return {
    id: Number.isFinite(id) ? id : 0,
    name: pickStr(r.name) ?? "",
    priority: Number(r.priority) || 0,
    display: asBool(r.display),
    active: asBool(r.active),
    desc: desc ?? undefined,
    seo_title: seo_title ?? undefined,
    seo_description: seo_description ?? undefined,
    creator_name: pickStr(r.creator_name) ?? undefined,
    created_date: firstStr(
      "created_date",
      "created_at",
      "date_created",
      "create_date",
    ),
    modifier_name: pickStr(r.modifier_name) ?? undefined,
    modified_date: firstStr(
      "modified_date",
      "updated_at",
      "modified_at",
      "update_date",
    ),
    parent_id: (() => {
      const v = r.parent_id;
      if (v == null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    })(),
    parent_name: pickStr(r.parent_name) ?? undefined,
    category_image_url:
      pickStr(r.category_image_url ?? r.image_url) ?? undefined,
  };
}

export default class CategoryAPI {
  private normalizeListResponse(
    params: GetCategoriesParams,
    raw: unknown,
  ): PaginatedAPIResponse<Category> {
    if (Array.isArray(raw)) {
      const list = raw.map(normalizeCategoryRecord);
      return {
        list,
        limit: params.limit ?? list.length,
        page_no: params.page_no ?? 1,
        total_count: list.length,
        total_pages: 1,
      };
    }
    const r = raw as Record<string, unknown>;
    const rows = r.list ?? r.items ?? r.data;
    const list = Array.isArray(rows)
      ? rows.map((row) => normalizeCategoryRecord(row))
      : [];

    const limit = Number(r.limit ?? params.limit ?? 30) || 30;
    const page_no =
      Number(r.page_no ?? r.pageNo ?? params.page_no ?? 1) || 1;
    const total_count = Number(
      r.total_count ?? r.totalCount ?? list.length,
    );
    const total_pages = Number(r.total_pages ?? r.totalPages ?? 1);

    return {
      limit,
      page_no,
      total_count,
      total_pages,
      list,
    };
  }

  listCategories = async (
    params: GetCategoriesParams,
  ): Promise<PaginatedAPIResponse<Category>> => {
    const queryParams = new URLSearchParams();
    /** Matches `categoryListQuerySchema` (same names as your Postman example). */
    if (params.page_no != null)
      queryParams.append("page_no", String(params.page_no));
    if (params.limit != null)
      queryParams.append("limit", String(params.limit));
    const sortBy = normalizeCategoryListSortBy(params.sort_by);
    queryParams.append("sort_by", sortBy);
    if (params.sort_type != null)
      queryParams.append("sort_type", String(params.sort_type));
    const term = params.search?.trim();
    if (term) {
      /** Backend: `search=…` for name/substring (and numeric as text search). */
      queryParams.append("search", term);
      /** Backend: `id=…` for exact category id (optional alongside search per your API). */
      if (/^\d+$/.test(term)) {
        queryParams.append("id", term);
      }
    }
    if (params.active !== undefined)
      queryParams.append("active", params.active ? "1" : "0");

    const qs = queryParams.toString();
    const response = await getAPI(`${BASE}${qs ? `?${qs}` : ""}`);
    let raw: unknown = response?.result;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const o = raw as Record<string, unknown>;
      /** Some APIs wrap again as `result.result` or put payload on `data`. */
      if (!("list" in o) && !("items" in o) && o.result && typeof o.result === "object")
        raw = o.result;
      else if (
        !("list" in o) &&
        !("items" in o) &&
        o.data &&
        typeof o.data === "object"
      )
        raw = o.data;
    }
    return this.normalizeListResponse(params, raw);
  };

  getCategoryById = async (id: number): Promise<Category> => {
    const response = await getAPI(`${BASE}/${id}`);
    return normalizeCategoryRecord(response.result);
  };

  createCategory = async (
    body: CategoryUpsertPayload,
  ): Promise<APIResponse> => {
    const response = await postAPI(BASE, body);
    return response;
  };

  updateCategory = async (
    id: number,
    body: CategoryUpsertPayload,
  ): Promise<APIResponse> => {
    const response = await putAPI(`${BASE}/${id}`, body);
    return response;
  };

  deleteCategory = async (id: number): Promise<APIResponse> => {
    const response = await deleteAPI(`${BASE}/${id}`);
    return response;
  };
}
